import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  AlertColor,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { BuildingType } from "./BuildingSelectionModal";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import {
  StaticIpRegex,
  StaticIpErrorMessage,
  IpV4Regex,
  IpV4ErrorMessage,
} from "../../../utils/ValidationHelper";
import {
  blankDoorDetailsValidationErrors,
  defaultSdc,
  DoorDetailsTable,
  DoorDetailsValidationErrors,
  SdcDetails,
} from "./DoorDetailsTable";
import {
  ErrorWithRetry,
  LoadingSpinner,
} from "../../molecules/LoadingAndError";

const DoorRegistrationPage: React.FC = () => {
  const { autoBuildingIdInUrl } = useParams();

  const [sdc, setSdc] = useState<SdcDetails>(defaultSdc);
  const [fetchedTs, setFetchedTs] = useState<number>(0);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [isRegistering, setIsRegistering] = useState(false);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [cancelConfirm, setCancelConfirm] = React.useState(false);
  const [doorCreatedConfirm, setDoorCreatedConfirm] = React.useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [isTouched, setIsTouched] = useState({
    sdcSerialNumber: false,
    doorPlace: false,
    memo: false,
    ap1IpConfigType: false,
    ap2IpConfigType: false,
    ap3IpConfigType: false,
    isLteEnabled: false,
    isWifiEnabled: false,
    ap1StaticIp: false,
    ap2StaticIp: false,
    ap3StaticIp: false,
  });

  const [validationErrors, setValidationErrors] =
    useState<DoorDetailsValidationErrors>(blankDoorDetailsValidationErrors);

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      doorPlace:
        isTouched.doorPlace &&
        sdc.doorPlace !== null &&
        sdc.doorPlace !== "" &&
        sdc.doorPlace.length > CHARACTER_LIMIT
          ? `設置場所は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      memo:
        isTouched.memo && sdc.memo !== "" && sdc.memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      isLteEnabled:
        sdc.isLteEnabled && !sdc.lteApn ? "APNを入力してください" : "",
      isWifiEnabled:
        sdc.isWifiEnabled &&
        (!sdc.ap1Ssid || !sdc.ap1Password) &&
        (!sdc.ap2Ssid || !sdc.ap2Password) &&
        (!sdc.ap3Ssid || !sdc.ap3Password)
          ? "最低一つのAP IP設定を入力してください"
          : "",
      ap1IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap1IpConfigType === "STATIC" &&
        (!sdc.ap1StaticIp || !sdc.ap1StaticGateWay || !sdc.ap1StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap2IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap2IpConfigType === "STATIC" &&
        (!sdc.ap2StaticIp || !sdc.ap2StaticGateWay || !sdc.ap2StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap3IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap3IpConfigType === "STATIC" &&
        (!sdc.ap3StaticIp || !sdc.ap3StaticGateWay || !sdc.ap3StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap1StaticIp:
        sdc.ap1StaticIp && !StaticIpRegex.test(sdc.ap1StaticIp)
          ? StaticIpErrorMessage
          : "",
      ap2StaticIp:
        sdc.ap2StaticIp && !StaticIpRegex.test(sdc.ap2StaticIp)
          ? StaticIpErrorMessage
          : "",
      ap3StaticIp:
        sdc.ap3StaticIp && !StaticIpRegex.test(sdc.ap3StaticIp)
          ? StaticIpErrorMessage
          : "",
      sciSignageIpAddr:
        sdc.sciSignageIpAddr && !IpV4Regex.test(sdc.sciSignageIpAddr)
          ? IpV4ErrorMessage
          : "",
      sdcSerialNumber:
        isTouched.sdcSerialNumber && !sdc.sdcSerialNumber
          ? "SDCSNを選択してください"
          : "",
    };

    setValidationErrors(errors);
  }, [isTouched, sdc]);

  const handleSelectBuilding = (selectedBuilding: BuildingType) => {
    setSdc((s) => ({
      ...s,
      building: {
        autoBuildingId: selectedBuilding.autoBuildingId,
        buildingManagementId: selectedBuilding.buildingManagementId,
        buildingId: selectedBuilding.buildingId,
        buildingName: selectedBuilding.buildingName,
        buildingAddress: selectedBuilding.buildingAddress,
      },
    }));
    setOpenBuildingModal(false);
  };

  const [openBuildingModal, setOpenBuildingModal] = useState(false);

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  // formEdited は最初は false であり、それを変更する関数が setFormEdited
  const [formEdited, setFormEdited] = useState(false);

  const navigate = useNavigate();

  const handleCancelClick = () => {
    // フォームが編集されているかどうかを確認する
    if (formEdited) {
      // フォームが編集されている場合は、移動する前に確認
      setCancelConfirm(true);
    } else {
      // フォームが編集されていない場合は、確認せずに移動
      navigate(-1);
    }
  };

  const fetchBuildingData = async () => {
    setIsLoading(true);
    setHasError(false);

    try {
      const jwtToken = await getJwtTokenFromIdToken();
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingIdInUrl}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setFetchedTs(Date.now());
      const data = await response.json();
      setSdc((s) => ({
        ...s,
        building: {
          autoBuildingId: data.autoBuildingId,
          buildingManagementId: data.buildingManagementId,
          buildingId: data.buildingId,
          buildingName: data.buildingName,
          buildingAddress: data.buildingAddress,
        },
      }));
    } catch (error) {
      console.error("Error fetching building data:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildingData();
  }, [autoBuildingIdInUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); //ページロード時にスクロール位置をリセット

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <ErrorWithRetry onRetry={fetchBuildingData} />;
  }

  const handleRegisterClick = async () => {
    if (!sdc.sdcSerialNumber || !sdc.doorPlace) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        sdcSerialNumber: !sdc.sdcSerialNumber
          ? `SDCSNを選択してください`
          : prevErrors.sdcSerialNumber,
        doorPlace: !sdc.doorPlace ? `設置場所は必須です` : prevErrors.doorPlace,
      }));
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return; // エラーがあるため、保存操作を中断
    }

    if (!sdc.isWifiEnabled && !sdc.isLteEnabled) {
      setSnackbarMessage("LTEまたはWi-Fiのどちらかを有効にしてください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return; // エラーがあるため、保存操作を中断
    }

    // エラーがある場合、保存を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    const requestData = {
      lastTsFetchedAt: fetchedTs,
      sdc: {
        shippingDate: sdc.shippingDate,
        isLteEnabled: sdc.isLteEnabled,
        lteApn: sdc.lteApn,
        lteId: sdc.lteId,
        ltePassword: sdc.ltePassword,
        lteAuthType: sdc.lteAuthType,
        isWifiEnabled: sdc.isWifiEnabled,
        defaultWifi: sdc.defaultWifi,
        ap1Ssid: sdc.ap1Ssid,
        ap1Password: sdc.ap1Password,
        ap1IpConfigType: sdc.ap1IpConfigType,
        ap1StaticIp: sdc.ap1StaticIp,
        ap1StaticGateWay: sdc.ap1StaticGateWay,
        ap1StaticDns: sdc.ap1StaticDns,
        ap2Ssid: sdc.ap2Ssid,
        ap2Password: sdc.ap2Password,
        ap2IpConfigType: sdc.ap2IpConfigType,
        ap2StaticIp: sdc.ap2StaticIp,
        ap2StaticGateWay: sdc.ap2StaticGateWay,
        ap2StaticDns: sdc.ap2StaticDns,
        ap3Ssid: sdc.ap3Ssid,
        ap3Password: sdc.ap3Password,
        ap3IpConfigType: sdc.ap3IpConfigType,
        ap3StaticIp: sdc.ap3StaticIp,
        ap3StaticGateWay: sdc.ap3StaticGateWay,
        ap3StaticDns: sdc.ap3StaticDns,
        isAalEnabled: sdc.isAalEnabled,
        isBleEnabled: sdc.isBleEnabled,
        isNfcEnabled: sdc.isNfcEnabled,
        isNfcCertRequired: sdc.isNfcCertRequired,
        isSciEnabled: sdc.isSciEnabled,
        isGuideBellEnabled: sdc.isGuideBellEnabled,
        isSpeakerEnabled: sdc.isSpeakerEnabled,
        isSignageEnabled: sdc.isSignageEnabled,
        doorPlace: sdc.doorPlace,
        installationDate: sdc.installationDate,
        // 課金開始日時が存在する場合は00:00:00を付与、ない場合はnull
        billingStartAt: sdc.billingStartAt
          ? new Date(`${sdc.billingStartAt}T00:00:00+09:00`).toISOString()
          : null,
        memo: sdc.memo,
        sciSignageIpAddr: sdc.sciSignageIpAddr,
        idInCluster: sdc.idInCluster,
        isDefaultOpenHalf: sdc.isDefaultOpenHalf,
        isPushButtonEnabled: sdc.isPushButtonEnabled,
        defaultDistanceNear: sdc.defaultDistanceNear,
        defaultDistanceFar: sdc.defaultDistanceFar,
        txPowerLevel: sdc.txPowerLevel,
        isSpeakerContactEnabled: sdc.isSpeakerContactEnabled,
        logMode: sdc.logMode,
        autoBuildingId:
          sdc.building === null ? null : sdc.building.autoBuildingId,
      },
    };
    setIsRegistering(true); // 登録処理開始

    try {
      // call updateSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + `/sdcs/${sdc.autoSdcId}?version=2`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.status === 409) {
        setSnackbarMessage(
          "編集中にSDC情報に更新がありました。画面の再読込をしてください。"
        );
        setAlertSeverity("error");
        setSnackbarOpen(true);
      } else if (response.ok) {
        // 更新成功の場合の処理
        console.log("SDC（ドア）の登録が成功しました。");
        setDoorCreatedConfirm(true);
      } else {
        // 更新失敗の場合の処理
        console.error("SDC（ドア）の登録に失敗しました。");
        setSnackbarMessage("SDC（ドア）の登録に失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("SDC（ドア）の登録時にエラーが発生しました:", error);
      setSnackbarMessage("SDC（ドア）の登録時にエラーが発生しました");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsRegistering(false); // 登録処理終了
    }
  };

  const handleSuccess = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("success");
    setSnackbarOpen(true);
  };

  const handleError = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("error");
    setSnackbarOpen(true);
  };

  const refreshSdcData = () => {
    // 登録ページでは実際のデータ更新は必要ないため、空の関数を用意
  };

  return (
    <CustomContainer maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <PageTitle title={"SDC（ドア）新規登録"} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleFormSubmit}>
            <DoorDetailsTable
              isRegistration={true}
              sdc={sdc}
              setSdc={setSdc}
              setFormEdited={setFormEdited}
              openBuildingModal={openBuildingModal}
              setOpenBuildingModal={setOpenBuildingModal}
              handleSelectBuilding={handleSelectBuilding}
              handleBlur={handleBlur}
              validationErrors={validationErrors}
              onSuccess={handleSuccess}
              onError={handleError}
              refreshSdcData={refreshSdcData}
              setFetchedTs={setFetchedTs}
            />
            <Grid
              item
              xs={12}
              marginTop={1}
              display="flex"
              justifyContent="flex-end"
              marginBottom={2}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelClick}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                onClick={handleRegisterClick}
                disabled={isRegistering}
                sx={{ marginLeft: 1 }}
              >
                {isRegistering ? <CircularProgress size={24} /> : "登録"}
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={doorCreatedConfirm}
        onClose={() => {
          setDoorCreatedConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/buildings/` + autoBuildingIdInUrl);
        }}
        title="確認"
        description="SDC（ドア）の登録が成功しました。"
        showCancelButton={false}
      />

      <ConfirmDialog
        open={cancelConfirm}
        onClose={() => {
          setCancelConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/doors`);
        }}
        title="確認"
        description="入力した内容が破棄されますが良いですか?"
      />
    </CustomContainer>
  );
};

export default DoorRegistrationPage;
